import { TILE, CELL } from './geo.js';

// The tile file format, and how a tile is cut into cells for streaming.
// A tile is { b: buildings, r: roads, w: water lines, a: water areas, l: land use }, every
// coordinate in decimetres from the tile's north-west corner. Today the file is plain JSON; the
// server, the simulation, the build scripts and the client worker all read it through here so a
// binary format later changes one file.
export function decodeTile(text) {
  return JSON.parse(text);
}

export function encodeTile(data) {
  return JSON.stringify(data);
}

export const CELLS = TILE / CELL; // cells along one side of a tile
const CELL_DM = CELL * 10; // a cell in decimetres

// Which cell along one axis a tile-local decimetre coordinate falls in.
export function cellIndex(v) {
  const c = Math.floor(v / CELL_DM);
  return c < 0 ? 0 : c >= CELLS ? CELLS - 1 : c;
}

// The mean of a polygon's vertices, as [x, z] in decimetres.
export function polygonCentre(p) {
  let x = 0;
  let z = 0;
  const n = p.length / 2;
  for (let i = 0; i < p.length; i += 2) {
    x += p[i];
    z += p[i + 1];
  }
  return [x / n, z / n];
}

// Cuts a polyline (flat [x, z, x, z, ...]) to the pieces inside the rectangle x0..x1 by z0..z1.
// Returns a list of flat polylines. A piece keeps the exact crossing point on the rectangle's
// edge, so the pieces of neighbouring cells meet without a gap.
export function clipPolyline(p, x0, z0, x1, z1) {
  const pieces = [];
  let piece = null;
  for (let i = 2; i < p.length; i += 2) {
    const ax = p[i - 2];
    const az = p[i - 1];
    const bx = p[i];
    const bz = p[i + 1];
    const dx = bx - ax;
    const dz = bz - az;
    // Liang-Barsky: the parameter range of the segment inside the rectangle
    let t0 = 0;
    let t1 = 1;
    const edges = [
      [-dx, ax - x0],
      [dx, x1 - ax],
      [-dz, az - z0],
      [dz, z1 - az],
    ];
    let out = false;
    for (const [q, r] of edges) {
      if (q === 0) {
        if (r < 0) {
          out = true;
          break;
        }
        continue;
      }
      const t = r / q;
      if (q < 0) {
        if (t > t1) {
          out = true;
          break;
        }
        if (t > t0) t0 = t;
      } else {
        if (t < t0) {
          out = true;
          break;
        }
        if (t < t1) t1 = t;
      }
    }
    if (out || t1 <= t0) {
      piece = null;
      continue;
    }
    const sx = ax + dx * t0;
    const sz = az + dz * t0;
    const ex = ax + dx * t1;
    const ez = az + dz * t1;
    if (!piece || t0 > 0) {
      piece = [sx, sz];
      pieces.push(piece);
    }
    piece.push(ex, ez);
    if (t1 < 1) piece = null;
  }
  return pieces.filter((q) => q.length >= 4);
}

// What one cell of a tile draws: the indices of the buildings whose centre lies in it, the pieces
// of road and water line inside it, the lakes whose centre lies in it, and the land-use polygons
// that touch it. Buildings and lakes are never split, so each is drawn exactly once.
export function cellContents(data, cx, cz) {
  const x0 = cx * CELL_DM;
  const z0 = cz * CELL_DM;
  const x1 = x0 + CELL_DM;
  const z1 = z0 + CELL_DM;
  const bi = [];
  for (let i = 0; i < data.b.length; i++) {
    const p = data.b[i].p;
    if (p.length < 6) continue;
    const [x, z] = polygonCentre(p);
    if (cellIndex(x) === cx && cellIndex(z) === cz) bi.push(i);
  }
  const r = [];
  for (const road of data.r) {
    for (const piece of clipPolyline(road.p, x0, z0, x1, z1)) r.push({ ...road, p: piece });
  }
  const w = [];
  for (const line of data.w) {
    for (const piece of clipPolyline(line.p, x0, z0, x1, z1)) w.push({ ...line, p: piece });
  }
  const a = data.a.filter((lake) => {
    const [x, z] = polygonCentre(lake.p);
    return cellIndex(x) === cx && cellIndex(z) === cz;
  });
  return { bi, r, w, a };
}
